import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMasterTransactionsByEmail, getMasterTransactions, getMasterTransactionsByStaffId } from '../services/allocationService';
import { getDisputesByEmail, Dispute, getDisputeRequestsByEmail } from '../services/disputeService';
import { getCashTipsByEmail, CashTip } from '../services/cashTipService';
import { getSettings } from '../services/settingsService';
import { getStaffByEmail, Staff } from '../services/staffService';
import { useFirebase } from '../contexts/FirebaseContext';
import { DollarSign, AlertCircle, Coins, FileCheck, X } from 'lucide-react';
import { MasterTransaction, DisputeRequest } from '../types';
import { formatCurrency } from '../lib/formatters';
import { TransactionHistory } from './EmployeeTransactionHistory';
import { EmployeePayPeriodTips } from './EmployeePayPeriodTips';
import { DisputeForm } from './DisputeForm';

export const EmployeeTipsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, tenantId, userName } = useFirebase();
  const [transactions, setTransactions] = useState<MasterTransaction[]>([]);
  const [allTransactions, setAllTransactions] = useState<MasterTransaction[]>([]);
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [cashTips, setCashTips] = useState<CashTip[]>([]);
  const [disputeRequests, setDisputeRequests] = useState<DisputeRequest[]>([]);
  const [staff, setStaff] = useState<Staff | null>(null);
  const [displayTipBreakdown, setDisplayTipBreakdown] = useState<boolean>(true);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'history' | 'payperiod'>('history');
  
  const [isDisputeModalOpen, setIsDisputeModalOpen] = useState(false);
  const [disputeContext, setDisputeContext] = useState<{ targetTransactionId?: string; shiftContext?: { date: string; shiftId: string } }>({});

  const handleOpenDispute = (context: { targetTransactionId?: string; shiftContext?: { date: string; shiftId: string } }) => {
    setDisputeContext(context);
    setIsDisputeModalOpen(true);
  };

  useEffect(() => {
    if (!user?.email || !tenantId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const [settings, staffData, tenantTxs, disputesData, disputeRequestsData, cashTipsData] = await Promise.all([
          getSettings(tenantId),
          getStaffByEmail(tenantId, user.email),
          getMasterTransactions(tenantId),
          getDisputesByEmail(tenantId, user.email.toLowerCase()),
          getDisputeRequestsByEmail(tenantId, user.email.toLowerCase()),
          getCashTipsByEmail(tenantId, user.email.toLowerCase())
        ]);
        
        let txData: MasterTransaction[] = [];
        if (staffData?.id) {
          const [byStaff, byEmail] = await Promise.all([
            getMasterTransactionsByStaffId(tenantId, staffData.id),
            getMasterTransactionsByEmail(tenantId, user.email.toLowerCase())
          ]);
          const txMap = new Map<string, MasterTransaction>();
          byStaff.forEach(t => t.id && txMap.set(t.id, t));
          byEmail.forEach(t => t.id && txMap.set(t.id, t));
          txData = Array.from(txMap.values());
        } else {
          txData = await getMasterTransactionsByEmail(tenantId, user.email.toLowerCase());
        }
        
        setTransactions(txData);
        setDisputes(disputesData);
        setCashTips(cashTipsData);
        setDisputeRequests(disputeRequestsData);
        setStaff(staffData);
        setAllTransactions(tenantTxs);
        if (settings) setDisplayTipBreakdown(settings.displayTipBreakdown);
      } catch (e) {
        console.error('Error fetching dashboard data:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user?.email, tenantId, isDisputeModalOpen]);

  const totalTips = transactions
    .filter(t => t.type === 'Allocated')
    .reduce((acc, t) => acc + (t.amount || 0), 0);
    
  const pendingDisputes = disputes.filter(d => d.status === 'Pending').length;
  const pendingCashTips = cashTips.filter(t => t.status === 'Pending').length;
  const pendingSignOffs = transactions.filter(t => (t.type === 'Allocated' || t.type === 'Submitted') && !t.employeeApproved).length;

  if (loading) return (
    <div className="flex items-center justify-center p-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>
  );

  return (
    <div className="space-y-10">
      <header className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-3 pb-2 border-b border-gray-100">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tighter uppercase italic">My Tip Earnings</h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1 font-medium">Real-time tip accumulation and transactional audit log.</p>
        </div>
        <div className="flex flex-col sm:items-end gap-1">
          <p className="text-base sm:text-lg font-bold text-gray-900">{staff?.name || userName || user?.email}</p>
          <div className="flex flex-wrap items-center gap-1.5">
            {staff?.posId && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-mono font-bold uppercase">
                POS ID: {staff.posId}
              </span>
            )}
            {staff?.id && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-200 text-slate-800 text-[10px] font-mono font-bold" title={`Full Staff ID: ${staff.id}`}>
                Staff ID: {staff.id.slice(0, 8)}
              </span>
            )}
            {user?.email && (
              <span className="text-[11px] text-gray-400 font-medium hidden sm:inline">
                {user.email}
              </span>
            )}
          </div>
        </div>
      </header>
      
      {/* 4 Summary Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-4 lg:gap-5">
        {/* Card 1: Total Tip Earnings */}
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all flex items-center gap-3.5 sm:gap-4 min-w-0">
          <div className="w-11 h-11 sm:w-12 sm:h-12 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center shrink-0 shadow-xs">
            <DollarSign className="w-5 h-5 sm:w-6 sm:h-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-[10px] sm:text-[11px] font-black text-gray-400 uppercase tracking-wider truncate">Total Tips</h3>
            <p className="text-xl sm:text-2xl lg:text-[24px] xl:text-[26px] font-black text-gray-900 tracking-tight leading-tight mt-0.5 truncate" title={formatCurrency(totalTips)}>
              {formatCurrency(totalTips)}
            </p>
            <span className="text-[11px] text-gray-400 font-medium block truncate">Allocated Earnings</span>
          </div>
        </div>

        {/* Card 2: Disputes & Inquiries */}
        <div 
          onClick={() => navigate('/disputes')} 
          className="bg-white p-4 sm:p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-amber-200 transition-all flex items-center gap-3.5 sm:gap-4 cursor-pointer min-w-0 group"
          title="Click to view Dispute Management"
        >
          <div className="w-11 h-11 sm:w-12 sm:h-12 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center shrink-0 shadow-xs group-hover:scale-105 transition-transform">
            <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-[10px] sm:text-[11px] font-black text-gray-400 uppercase tracking-wider truncate flex items-center gap-1">
              Disputes
              <span className="text-amber-600 text-[10px]">→</span>
            </h3>
            <p className="text-xl sm:text-2xl lg:text-[24px] xl:text-[26px] font-black text-gray-900 tracking-tight leading-tight mt-0.5 truncate">
              {pendingDisputes}
            </p>
            <span className={`text-[11px] font-semibold block truncate ${pendingDisputes > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
              {pendingDisputes > 0 ? `${pendingDisputes} Pending` : 'All Resolved ✓'}
            </span>
          </div>
        </div>

        {/* Card 3: Pending Cash Tips */}
        <div 
          onClick={() => navigate('/tips')} 
          className="bg-white p-4 sm:p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-blue-200 transition-all flex items-center gap-3.5 sm:gap-4 cursor-pointer min-w-0 group"
          title="Click to submit or view Cash Tips"
        >
          <div className="w-11 h-11 sm:w-12 sm:h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center shrink-0 shadow-xs group-hover:scale-105 transition-transform">
            <Coins className="w-5 h-5 sm:w-6 sm:h-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-[10px] sm:text-[11px] font-black text-gray-400 uppercase tracking-wider truncate flex items-center gap-1">
              Cash Submissions
              <span className="text-blue-600 text-[10px]">→</span>
            </h3>
            <p className="text-xl sm:text-2xl lg:text-[24px] xl:text-[26px] font-black text-gray-900 tracking-tight leading-tight mt-0.5 truncate">
              {pendingCashTips}
            </p>
            <span className="text-[11px] text-blue-600 font-semibold block truncate">
              {pendingCashTips > 0 ? `${pendingCashTips} Pending Review` : 'Submit Cash Tip'}
            </span>
          </div>
        </div>

        {/* Card 4: Sign-Offs */}
        <div 
          onClick={() => navigate('/approvals')} 
          className="bg-white p-4 sm:p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all flex items-center gap-3.5 sm:gap-4 cursor-pointer min-w-0 group"
          title="Click to review and sign off for IRS compliance"
        >
          <div className={`w-11 h-11 sm:w-12 sm:h-12 ${pendingSignOffs > 0 ? 'bg-indigo-50 text-indigo-600' : 'bg-emerald-50 text-emerald-600'} rounded-xl flex items-center justify-center shrink-0 shadow-xs group-hover:scale-105 transition-transform`}>
            <FileCheck className="w-5 h-5 sm:w-6 sm:h-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-[10px] sm:text-[11px] font-black text-gray-400 uppercase tracking-wider truncate flex items-center gap-1">
              Sign-Offs
              <span className="text-indigo-600 text-[10px]">→</span>
            </h3>
            <p className="text-xl sm:text-2xl lg:text-[24px] xl:text-[26px] font-black text-gray-900 tracking-tight leading-tight mt-0.5 truncate">
              {pendingSignOffs}
            </p>
            <span className={`text-[11px] font-semibold block truncate ${pendingSignOffs > 0 ? 'text-indigo-600' : 'text-emerald-600'}`}>
              {pendingSignOffs > 0 ? `${pendingSignOffs} Pending Signature` : 'Up to Date ✓'}
            </span>
          </div>
        </div>
      </div>
      
      <div className="flex border-b border-gray-200">
        <button 
          onClick={() => setActiveTab('history')}
          className={`px-6 py-3 text-sm font-bold uppercase tracking-widest ${activeTab === 'history' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
        >
          Tip History
        </button>
        <button 
          onClick={() => setActiveTab('payperiod')}
          className={`px-6 py-3 text-sm font-bold uppercase tracking-widest ${activeTab === 'payperiod' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
        >
          Benchmark Trends
        </button>
      </div>

      {activeTab === 'history' ? (
        <TransactionHistory transactions={transactions} displayTipBreakdown={displayTipBreakdown} onOpenDispute={handleOpenDispute} />
      ) : (
        <EmployeePayPeriodTips transactions={transactions} allTransactions={allTransactions} onOpenDispute={handleOpenDispute} />
      )}
      
      {isDisputeModalOpen && tenantId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-3xl shadow-2xl relative animate-in zoom-in-95 duration-300">
            <button 
              onClick={() => setIsDisputeModalOpen(false)}
              className="absolute top-6 right-6 p-2 hover:bg-gray-100 rounded-full text-gray-400 z-10 transition-colors"
              title="Close form"
            >
              <X size={24} />
            </button>
            <div className="p-2 md:p-4">
              <DisputeForm 
                tenantId={tenantId} 
                prefillTip={
                  disputeContext.targetTransactionId 
                    ? transactions.find(t => t.id === disputeContext.targetTransactionId) || { id: disputeContext.targetTransactionId }
                    : disputeContext.shiftContext
                }
                onSuccess={() => {
                  setIsDisputeModalOpen(false);
                }}
              />
            </div>
            <div className="px-8 pb-8 flex justify-end">
              <button 
                onClick={() => setIsDisputeModalOpen(false)}
                className="px-6 py-3 text-xs font-black uppercase tracking-widest text-gray-500 hover:text-gray-700 bg-gray-100 rounded-xl transition-all"
              >
                Cancel / Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
